import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  PlusCircle, Star, PencilSimple, Trash, Eye, EyeSlash, X as XIcon,
  Sparkle, Warning, CheckCircle, DotsSixVertical, QrCode, ThumbsUp,
  ThumbsDown, Printer,
} from "@phosphor-icons/react";
import { QRCodeSVG } from "qrcode.react";
import client from "../lib/api";

/*
  AdminTestimonialsPage — CRUD for customer testimonials shown on /customers.
  Two types:
    - featured: full case study card (persona + metrics + pain + solution)
    - quote:    short pull-quote for the wall

  All docs start in `draft` — hit Publish to make them live on /customers.
  Route: /app/admin/testimonials  (admin-only via require_admin backend)
*/

const ICON_OPTIONS = [
  { value: "clock", label: "Clock (time saved)" },
  { value: "money", label: "Dollar (revenue)" },
  { value: "chart", label: "Chart (growth %)" },
  { value: "warning", label: "Warning (issues cut)" },
  { value: "chat", label: "Chat (comms)" },
  { value: "trend", label: "Trend (rates up)" },
];

const EMPTY_TESTIMONIAL = {
  type: "quote",
  status: "draft",
  quote: "",
  person: "",
  role: "",
  persona: "",
  duration: "",
  metrics: [],
  pain: "",
  solution: "",
  sort_order: 0,
};

export default function AdminTestimonialsPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // testimonial being edited or new
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await client.get("/admin/testimonials");
      setItems(data.testimonials || []);
    } catch {
      toast.error("Couldn't load testimonials");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const startNew = (type = "quote") => setEditing({ ...EMPTY_TESTIMONIAL, type });
  const startEdit = (t) => setEditing({ ...EMPTY_TESTIMONIAL, ...t });
  const closeEdit = () => setEditing(null);

  const save = async () => {
    if (!editing) return;
    const payload = { ...editing };
    // Strip out empty metrics
    payload.metrics = (payload.metrics || []).filter((m) => m && m.label && m.value);
    setSaving(true);
    try {
      if (payload.id) {
        // Full replace
        const patch = { ...payload };
        delete patch.id;
        delete patch.created_at;
        delete patch.updated_at;
        delete patch.created_by;
        delete patch.updated_by;
        await client.patch(`/admin/testimonials/${payload.id}`, patch);
        toast.success("Updated");
      } else {
        const create = { ...payload };
        delete create.id;
        await client.post("/admin/testimonials", create);
        toast.success("Created (draft)");
      }
      closeEdit();
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const publish = async (t) => {
    try {
      await client.post(`/admin/testimonials/${t.id}/publish`);
      toast.success("Published — live on /customers");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Publish failed");
    }
  };

  const unpublish = async (t) => {
    try {
      await client.post(`/admin/testimonials/${t.id}/unpublish`);
      toast.success("Moved back to draft");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Unpublish failed");
    }
  };

  const remove = async (t) => {
    if (!window.confirm(`Delete ${t.person}'s testimonial? This can't be undone.`)) return;
    try {
      await client.delete(`/admin/testimonials/${t.id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const grouped = useMemo(() => {
    const featured = items.filter((t) => t.type === "featured");
    const quotes = items.filter((t) => t.type === "quote");
    const published = items.filter((t) => t.status === "published").length;
    return { featured, quotes, published };
  }, [items]);

  return (
    <div className="max-w-6xl mx-auto p-4 sm:p-6 space-y-6" data-testid="admin-testimonials-page">
      <div className="flex items-start justify-between gap-4 flex-col sm:flex-row">
        <div>
          <div className="overline mb-1 flex items-center gap-2"><Star size={11} weight="fill" /> SOCIAL PROOF</div>
          <h1 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight">Customer testimonials</h1>
          <p className="text-neutral-500 text-sm mt-1">
            Manage what shows on <a href="/customers" target="_blank" rel="noreferrer" className="text-[#FF9764] hover:text-[#FFB08A]">the /customers page</a>. Publish flips it live instantly.
            <span className="ml-2 text-neutral-400">
              <b className="text-white">{grouped.published}</b> published · <b className="text-white">{items.length}</b> total
            </span>
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => startNew("quote")}
            data-testid="new-quote-btn"
            className="text-xs px-3 py-2 rounded-md border border-white/10 text-neutral-300 hover:text-white hover:border-white/30 inline-flex items-center gap-1.5"
          >
            <PlusCircle size={13} weight="fill" /> New quote
          </button>
          <button
            onClick={() => startNew("featured")}
            data-testid="new-featured-btn"
            className="text-xs px-3 py-2 rounded-md bg-[#FF5A1F] text-black font-semibold hover:bg-[#FF7038] inline-flex items-center gap-1.5"
          >
            <Sparkle size={13} weight="fill" /> New case study
          </button>
        </div>
      </div>

      {loading ? (
        <div className="text-neutral-500 text-sm">Loading…</div>
      ) : items.length === 0 ? (
        <>
          <PendingPhotosPanel onReload={load} />
          <PilotQuoteInvitesPanel />
          <EmptyState onNew={() => startNew("featured")} />
        </>
      ) : (
        <>
          <PendingPhotosPanel onReload={load} />
          <PilotQuoteInvitesPanel />
          <Section title="Featured case studies" count={grouped.featured.length}>
            {grouped.featured.map((t) => (
              <Row
                key={t.id}
                t={t}
                onEdit={startEdit}
                onDelete={remove}
                onPublish={publish}
                onUnpublish={unpublish}
              />
            ))}
            {grouped.featured.length === 0 && (
              <div className="text-xs text-neutral-500 italic px-2 py-3">
                No case studies yet — click "New case study" to add one.
              </div>
            )}
          </Section>

          <Section title="Short quotes" count={grouped.quotes.length}>
            {grouped.quotes.map((t) => (
              <Row
                key={t.id}
                t={t}
                onEdit={startEdit}
                onDelete={remove}
                onPublish={publish}
                onUnpublish={unpublish}
              />
            ))}
            {grouped.quotes.length === 0 && (
              <div className="text-xs text-neutral-500 italic px-2 py-3">
                No quotes yet — click "New quote" to add one.
              </div>
            )}
          </Section>
        </>
      )}

      {editing && (
        <EditModal
          t={editing}
          setT={setEditing}
          onClose={closeEdit}
          onSave={save}
          saving={saving}
        />
      )}
    </div>
  );
}

/* ─── SECTION WRAPPER ─── */
function Section({ title, count, children }) {
  return (
    <div className="card-surface p-4 sm:p-5" data-testid={`testimonials-section-${title.replace(/\s+/g, "-").toLowerCase()}`}>
      <div className="flex items-center gap-2 text-xs text-neutral-400 mb-3">
        <div className="overline">{title}</div>
        <span className="text-neutral-600">({count})</span>
      </div>
      <div className="divide-y divide-white/5">{children}</div>
    </div>
  );
}

/* ─── ROW ─── */
function Row({ t, onEdit, onDelete, onPublish, onUnpublish }) {
  const isPublished = t.status === "published";
  return (
    <div className="py-3 px-2 flex items-start gap-3" data-testid={`testimonial-row-${t.id}`}>
      <DotsSixVertical size={14} className="text-neutral-700 mt-2 shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="text-sm text-white font-medium truncate">{t.person}</div>
          <div className="text-xs text-neutral-500 truncate">{t.role}</div>
          <span
            className={`text-[10px] px-1.5 py-0.5 rounded-full uppercase tracking-wider font-semibold ${
              isPublished
                ? "bg-emerald-500/15 text-emerald-400 border border-emerald-500/25"
                : "bg-neutral-500/15 text-neutral-400 border border-white/10"
            }`}
          >
            {isPublished ? "Live" : "Draft"}
          </span>
          {t.type === "featured" && (
            <span className="text-[10px] px-1.5 py-0.5 rounded-full uppercase tracking-wider font-semibold bg-[#FF5A1F]/15 text-[#FF9764] border border-[#FF5A1F]/30">
              Case study
            </span>
          )}
        </div>
        <div className="text-xs text-neutral-500 mt-1 line-clamp-2 italic">"{t.quote}"</div>
      </div>
      <div className="flex items-center gap-1 shrink-0">
        {isPublished ? (
          <button
            onClick={() => onUnpublish(t)}
            title="Move back to draft"
            data-testid={`unpublish-${t.id}`}
            className="px-2 py-1 rounded text-xs border border-white/10 text-neutral-300 hover:text-white hover:border-white/30 inline-flex items-center gap-1"
          >
            <EyeSlash size={12} /> Draft
          </button>
        ) : (
          <button
            onClick={() => onPublish(t)}
            title="Publish live on /customers"
            data-testid={`publish-${t.id}`}
            className="px-2 py-1 rounded text-xs bg-emerald-500/20 border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/30 inline-flex items-center gap-1"
          >
            <Eye size={12} /> Publish
          </button>
        )}
        <button
          onClick={() => onEdit(t)}
          data-testid={`edit-${t.id}`}
          className="px-2 py-1 rounded text-xs border border-white/10 text-neutral-300 hover:text-white hover:border-white/30 inline-flex items-center gap-1"
        >
          <PencilSimple size={12} /> Edit
        </button>
        <button
          onClick={() => onDelete(t)}
          data-testid={`delete-${t.id}`}
          className="px-2 py-1 rounded text-xs border border-red-500/25 text-red-400 hover:bg-red-500/10 inline-flex items-center gap-1"
        >
          <Trash size={12} />
        </button>
      </div>
    </div>
  );
}

/* ─── EMPTY STATE ─── */
function EmptyState({ onNew }) {
  return (
    <div className="card-surface p-10 sm:p-14 text-center" data-testid="testimonials-empty">
      <Star size={38} weight="fill" className="text-[#FF5A1F]/60 mx-auto mb-4" />
      <div className="text-lg font-display font-semibold">No testimonials yet.</div>
      <p className="text-sm text-neutral-400 mt-2 max-w-md mx-auto leading-relaxed">
        Right now <a href="/customers" target="_blank" rel="noreferrer" className="text-[#FF9764] hover:text-[#FFB08A]">/customers</a> shows the built-in seed content. Add a real customer quote or case study
        to swap it out on the live page.
      </p>
      <button
        onClick={onNew}
        className="mt-6 rounded-full py-2.5 px-5 text-sm font-semibold bg-[#FF5A1F] text-black hover:bg-[#FF7038] inline-flex items-center gap-2"
      >
        <PlusCircle size={14} weight="fill" /> Add your first case study
      </button>
    </div>
  );
}

/* ─── EDIT / CREATE MODAL ─── */
function EditModal({ t, setT, onClose, onSave, saving }) {
  const isNew = !t.id;
  const isFeatured = t.type === "featured";

  const setField = (k) => (e) => setT({ ...t, [k]: e.target.value });

  const addMetric = () => setT({
    ...t,
    metrics: [...(t.metrics || []), { icon: "chart", label: "", value: "" }],
  });
  const removeMetric = (idx) => setT({
    ...t,
    metrics: (t.metrics || []).filter((_, i) => i !== idx),
  });
  const updateMetric = (idx, key, value) => setT({
    ...t,
    metrics: (t.metrics || []).map((m, i) => i === idx ? { ...m, [key]: value } : m),
  });

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto p-4 sm:p-8"
      data-testid="testimonial-edit-modal"
    >
      <div className="fixed inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-[#0F0F0F] border border-white/10 rounded-2xl w-full max-w-2xl shadow-2xl">
        <div className="flex items-start justify-between p-6 border-b border-white/10">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-[#FF5A1F] font-semibold mb-1">
              {isNew ? "New" : "Edit"} · {isFeatured ? "Case study" : "Short quote"}
            </div>
            <h2 className="text-xl font-display font-semibold text-white">
              {t.person || (isFeatured ? "Case study details" : "Pull quote")}
            </h2>
          </div>
          <button onClick={onClose} className="text-neutral-500 hover:text-white shrink-0" data-testid="close-edit-modal">
            <XIcon size={20} />
          </button>
        </div>

        <div className="p-6 space-y-5">
          {/* Person + role */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Person (first name + last initial)" required>
              <input
                value={t.person}
                onChange={setField("person")}
                placeholder="Marcus R."
                data-testid="input-person"
                className={inputCls}
                maxLength={80}
              />
            </Field>
            <Field label="Role" required>
              <input
                value={t.role}
                onChange={setField("role")}
                placeholder="Fleet Owner · Flatbed, 22 trucks · TX"
                data-testid="input-role"
                className={inputCls}
                maxLength={120}
              />
            </Field>
          </div>

          {/* Quote */}
          <Field label="Quote" required hint="Between 20 and 800 characters. Speak like a trucker, not a marketer.">
            <textarea
              value={t.quote}
              onChange={setField("quote")}
              rows={isFeatured ? 5 : 3}
              placeholder='"We stopped losing loads because someone forgot to update the check-call thread…"'
              data-testid="input-quote"
              className={`${inputCls} resize-y`}
              maxLength={800}
            />
          </Field>

          {/* Featured-only fields */}
          {isFeatured && (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field label="Persona" required hint="Fleet-type + size + region.">
                  <input
                    value={t.persona}
                    onChange={setField("persona")}
                    placeholder="Flatbed carrier · 22 trucks · Central TX"
                    data-testid="input-persona"
                    className={inputCls}
                    maxLength={160}
                  />
                </Field>
                <Field label="Duration" hint="How long they've been on the platform.">
                  <input
                    value={t.duration}
                    onChange={setField("duration")}
                    placeholder="5 months on FreightPilot AI"
                    data-testid="input-duration"
                    className={inputCls}
                    maxLength={80}
                  />
                </Field>
              </div>

              <Field label="The pain" required hint="One short sentence — what broke before.">
                <input
                  value={t.pain}
                  onChange={setField("pain")}
                  placeholder="Two dispatchers drowning in check-call texts. Missed detention windows."
                  data-testid="input-pain"
                  className={inputCls}
                  maxLength={300}
                />
              </Field>
              <Field label="The fix" required hint="Which FreightPilot feature specifically solved it.">
                <input
                  value={t.solution}
                  onChange={setField("solution")}
                  placeholder="AI Copilot pulls driver texts into one thread + drafts detention invoices."
                  data-testid="input-solution"
                  className={inputCls}
                  maxLength={300}
                />
              </Field>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-xs uppercase tracking-widest text-neutral-500">
                    Metrics <span className="text-red-400">*</span>
                  </label>
                  <button
                    onClick={addMetric}
                    data-testid="add-metric-btn"
                    className="text-xs text-[#FF9764] hover:text-[#FFB08A] inline-flex items-center gap-1"
                  >
                    <PlusCircle size={12} weight="fill" /> Add metric
                  </button>
                </div>
                <p className="text-[11px] text-neutral-500 mb-3">Featured case studies need at least 2 metrics — icon + label + value.</p>
                <div className="space-y-2">
                  {(t.metrics || []).map((m, idx) => (
                    <div key={idx} className="grid grid-cols-12 gap-2 items-start" data-testid={`metric-row-${idx}`}>
                      <select
                        value={m.icon}
                        onChange={(e) => updateMetric(idx, "icon", e.target.value)}
                        className={`col-span-3 ${inputCls}`}
                      >
                        {ICON_OPTIONS.map((o) => (
                          <option key={o.value} value={o.value}>{o.label}</option>
                        ))}
                      </select>
                      <input
                        value={m.label}
                        onChange={(e) => updateMetric(idx, "label", e.target.value)}
                        placeholder="Dispatcher hours saved / wk"
                        className={`col-span-5 ${inputCls}`}
                        maxLength={60}
                      />
                      <input
                        value={m.value}
                        onChange={(e) => updateMetric(idx, "value", e.target.value)}
                        placeholder="22 hrs"
                        className={`col-span-3 ${inputCls}`}
                        maxLength={40}
                      />
                      <button
                        onClick={() => removeMetric(idx)}
                        className="col-span-1 text-red-400 hover:bg-red-500/10 rounded p-1.5 self-start"
                        data-testid={`remove-metric-${idx}`}
                      >
                        <Trash size={12} />
                      </button>
                    </div>
                  ))}
                  {(!t.metrics || t.metrics.length === 0) && (
                    <div className="text-xs text-red-400/80 flex items-center gap-2 py-2 px-3 rounded-md border border-red-500/20 bg-red-500/[0.03]">
                      <Warning size={13} /> Featured case studies need at least 2 metrics
                    </div>
                  )}
                </div>
              </div>
            </>
          )}

          {/* Sort order */}
          <div className="grid grid-cols-2 gap-3">
            <Field label="Sort order" hint="Lower = shown first (0-100).">
              <input
                type="number"
                min="0"
                max="1000"
                value={t.sort_order}
                onChange={(e) => setT({ ...t, sort_order: parseInt(e.target.value || "0", 10) })}
                className={inputCls}
              />
            </Field>
            <Field label="Status">
              <select
                value={t.status}
                onChange={setField("status")}
                data-testid="input-status"
                className={inputCls}
              >
                <option value="draft">Draft (hidden)</option>
                <option value="published">Published (live)</option>
              </select>
            </Field>
          </div>
        </div>

        <div className="p-6 border-t border-white/10 flex items-center justify-end gap-2">
          <button
            onClick={onClose}
            className="rounded-full py-2.5 px-5 text-sm border border-white/15 text-neutral-300 hover:bg-white/5"
          >
            Cancel
          </button>
          <button
            onClick={onSave}
            disabled={saving}
            data-testid="save-testimonial-btn"
            className="rounded-full py-2.5 px-5 text-sm font-semibold bg-[#FF5A1F] text-black hover:bg-[#FF7038] disabled:opacity-50 inline-flex items-center gap-2"
          >
            {saving ? "Saving…" : (
              <><CheckCircle size={13} weight="fill" /> {isNew ? "Create" : "Save changes"}</>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─── Small helpers ─── */
const inputCls =
  "w-full bg-white/[0.03] border border-white/10 rounded-lg text-white text-sm py-2 px-3 focus:outline-none focus:border-[#FF5A1F]/60";

function Field({ label, required, hint, children }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-widest text-neutral-500 mb-2">
        {label} {required && <span className="text-red-400">*</span>}
      </label>
      {children}
      {hint && <div className="text-[11px] text-neutral-500 mt-1 leading-snug">{hint}</div>}
    </div>
  );
}


/* ─── Pilot Quote Invites panel ─── */
function PilotQuoteInvitesPanel() {
  const [form, setForm] = useState({ email: "", person_name: "", company_name: "", send_email: false });
  const [creating, setCreating] = useState(false);
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copiedToken, setCopiedToken] = useState(null);
  const [expandedQrToken, setExpandedQrToken] = useState(null);
  const [vinylBusy, setVinylBusy] = useState(null);
  const [vinylKit, setVinylKit] = useState(null);

  const loadInvites = async () => {
    setLoading(true);
    try {
      const [invitesResp, kitResp] = await Promise.all([
        client.get("/admin/pilot-quote-invites"),
        client.get("/admin/pilot-quote-invites/vinyl-kit"),
      ]);
      setInvites(invitesResp.data.invites || []);
      setVinylKit(kitResp.data);
    } catch (e) {
      // silent — panel just won't populate
    } finally {
      setLoading(false);
    }
  };

  const toggleVinyl = async (inv) => {
    setVinylBusy(inv.token);
    const nextState = !inv.vinyl_priority;
    try {
      await client.post(`/admin/pilot-quote-invites/${inv.token}/vinyl`, {
        vinyl_priority: nextState,
      });
      toast.success(nextState ? "Added to vinyl kit" : "Removed from vinyl kit");
      await loadInvites();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't update vinyl priority");
    } finally {
      setVinylBusy(null);
    }
  };

  // Handler for the "Order via Sticker Mule" button. Stamps
  // `vinyl_ordered_at` on all unordered marked invites before opening
  // Sticker Mule so ops has a paper-trail of what was sent + won't
  // accidentally re-order the same batch.
  const handleOrderVinyl = async () => {
    if (!vinylKit || vinylKit.unordered_count === 0) return;
    try {
      const { data } = await client.post("/admin/pilot-quote-invites/vinyl-kit/mark-ordered");
      toast.success(
        `Order log stamped · ${data.newly_marked} pilot${data.newly_marked === 1 ? "" : "s"} recorded`
      );
      // Open Sticker Mule in a new tab immediately after.
      window.open(vinylKit.sticker_mule_url, "_blank", "noopener,noreferrer");
      // Refresh so the chips flip to ordered state.
      await loadInvites();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't stamp the order log");
    }
  };

  useEffect(() => { loadInvites(); }, []);

  const createInvite = async (e) => {
    e.preventDefault();
    if (!form.email.trim()) {
      toast.error("Email is required");
      return;
    }
    setCreating(true);
    try {
      const { data } = await client.post("/admin/pilot-quote-invites", {
        email: form.email.trim(),
        person_name: form.person_name.trim() || undefined,
        company_name: form.company_name.trim() || undefined,
        send_email: form.send_email,
      });
      toast.success(
        data.email_result?.sent
          ? "Invite created + emailed to pilot"
          : "Invite link created — copy it below"
      );
      setForm({ email: "", person_name: "", company_name: "", send_email: false });
      loadInvites();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create invite");
    } finally {
      setCreating(false);
    }
  };

  const copyLink = async (token, url) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopiedToken(token);
      toast.success("Link copied");
      setTimeout(() => setCopiedToken(null), 2000);
    } catch (e) {
      toast.error("Couldn't copy — long-press the link instead");
    }
  };

  // Bulk print: opens a NEW window with a print-optimized 2×3 grid (6
  // invites per A4 page). We render QRs to SVG strings via qrcode.react's
  // renderToStaticMarkup then inline them so the print window doesn't
  // need React. `window.print()` fires after images/QRs paint.
  const openPrintSheet = async (invitesToPrint) => {
    if (!invitesToPrint.length) return;
    // Generate QR SVGs synchronously — qrcode.react ships a QRCodeSVG
    // React component, but for a standalone doc we need pure SVG. We
    // use the QR data-URL library included with qrcode.react via
    // renderToString from react-dom/server. Simpler path: use the same
    // component but rendered ad-hoc into a hidden container, then
    // extract innerHTML.
    const { renderToStaticMarkup } = await import("react-dom/server");
    const cards = invitesToPrint.map((inv) => {
      const svg = renderToStaticMarkup(
        <QRCodeSVG value={inv.submit_url} size={180} level="M" includeMargin={false} />
      );
      const name = (inv.invite_person_name || "").trim();
      const company = (inv.invite_company_name || "").trim();
      const heading = name || inv.email;
      const sub = [company, name ? inv.email : null].filter(Boolean).join(" · ");
      return `
        <div class="card">
          <div class="qr">${svg}</div>
          <div class="text">
            <div class="brand">FreightPilot AI · Founding Pilot</div>
            <div class="heading">${heading}</div>
            ${sub ? `<div class="sub">${sub}</div>` : ""}
            <div class="cta">Scan to share your 90-second story</div>
          </div>
        </div>
      `;
    }).join("");

    const html = `<!doctype html>
<html><head><meta charset="utf-8"><title>Pilot Invite QR Sheet · FreightPilot AI</title>
<style>
  @page { size: A4; margin: 12mm; }
  html, body { margin: 0; padding: 0; background: #fff; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Arial, sans-serif; color: #111; }
  .sheet { display: grid; grid-template-columns: 1fr 1fr; grid-auto-rows: 1fr; gap: 8mm; page-break-inside: auto; }
  .card {
    border: 1px dashed #ccc; border-radius: 6mm;
    padding: 6mm; display: flex; align-items: center; gap: 5mm;
    page-break-inside: avoid; break-inside: avoid;
  }
  .qr { width: 45mm; height: 45mm; flex: 0 0 auto; display: flex; align-items: center; justify-content: center; }
  .qr svg { width: 100%; height: 100%; }
  .text { flex: 1; min-width: 0; }
  .brand { font-size: 9pt; font-weight: 700; color: #FF5A1F; text-transform: uppercase; letter-spacing: 0.08em; margin-bottom: 2mm; }
  .heading { font-size: 12pt; font-weight: 700; color: #111; word-break: break-word; }
  .sub { font-size: 9pt; color: #666; margin-top: 1mm; word-break: break-word; }
  .cta { font-size: 8.5pt; color: #333; margin-top: 3mm; line-height: 1.4; }
  .header { text-align: center; margin-bottom: 6mm; }
  .header h1 { font-size: 14pt; margin: 0 0 1mm; color: #111; }
  .header p { font-size: 9pt; color: #666; margin: 0; }
  @media print { .noprint { display: none !important; } }
  .noprint { position: fixed; top: 8px; right: 8px; padding: 8px 14px; background: #FF5A1F; color: #fff; border: none; border-radius: 999px; font-weight: 700; cursor: pointer; font-size: 11pt; }
</style></head>
<body>
  <button class="noprint" onclick="window.print()">Print now →</button>
  <div class="header">
    <h1>FreightPilot AI · Pilot Quote Invites</h1>
    <p>Print, cut along the dashed borders, and hand out at events. Each QR jumps to a personal 90-second quote form.</p>
  </div>
  <div class="sheet">${cards}</div>
  <script>window.addEventListener('load', () => { setTimeout(() => window.print(), 800); });</script>
</body></html>`;
    const w = window.open("", "_blank", "width=900,height=1200");
    if (!w) {
      toast.error("Pop-up blocked — please allow pop-ups for the print sheet.");
      return;
    }
    w.document.open();
    w.document.write(html);
    w.document.close();
  };

  return (
    <div
      data-testid="pilot-quote-invites-panel"
      className="rounded-2xl border border-white/10 bg-white/[0.02] p-5"
    >
      <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
        <div>
          <div className="overline mb-1 flex items-center gap-2">
            <Sparkle size={11} weight="fill" /> PILOT QUOTES · SELF-SERVE
          </div>
          <h3 className="font-display text-lg font-semibold">Ask a pilot for a quote (no middleman)</h3>
          <p className="text-neutral-500 text-xs mt-1 leading-relaxed max-w-xl">
            Create a public link a pilot can open to drop their own quote + photo.
            Emails the ask automatically if you tick "send email", else copy the
            link and share via WhatsApp/LinkedIn/in-person.
          </p>
        </div>
        {invites.filter(i => i.status === "pending").length > 0 && (
          <button
            type="button"
            onClick={() => openPrintSheet(invites.filter(i => i.status === "pending"))}
            data-testid="pilot-invite-print-sheet-btn"
            className="text-[10px] uppercase tracking-widest text-neutral-400 hover:text-white border border-white/15 hover:border-[#FF5A1F]/40 rounded-full px-3 py-1.5 transition-colors inline-flex items-center gap-1.5 shrink-0"
            title="Open a print-ready sheet of QR codes (6 per A4 page)"
          >
            <Printer size={11} weight="bold" /> Print QR sheet
          </button>
        )}
      </div>

      <form onSubmit={createInvite} className="grid grid-cols-1 sm:grid-cols-6 gap-2 mb-4">
        <input
          type="email"
          placeholder="pilot@example.com *"
          value={form.email}
          onChange={(e) => setForm({ ...form, email: e.target.value })}
          data-testid="pilot-invite-email"
          required
          className="sm:col-span-2 rounded-md bg-black/40 border border-white/10 px-3 py-2 text-sm text-white focus:border-[#FF5A1F]/50 focus:outline-none placeholder:text-neutral-600"
        />
        <input
          type="text"
          placeholder="Name (optional)"
          value={form.person_name}
          onChange={(e) => setForm({ ...form, person_name: e.target.value })}
          data-testid="pilot-invite-name"
          className="sm:col-span-2 rounded-md bg-black/40 border border-white/10 px-3 py-2 text-sm text-white focus:border-[#FF5A1F]/50 focus:outline-none placeholder:text-neutral-600"
        />
        <input
          type="text"
          placeholder="Company (optional)"
          value={form.company_name}
          onChange={(e) => setForm({ ...form, company_name: e.target.value })}
          data-testid="pilot-invite-company"
          className="sm:col-span-2 rounded-md bg-black/40 border border-white/10 px-3 py-2 text-sm text-white focus:border-[#FF5A1F]/50 focus:outline-none placeholder:text-neutral-600"
        />
        <label className="sm:col-span-4 flex items-center gap-2 text-xs text-neutral-400 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={form.send_email}
            onChange={(e) => setForm({ ...form, send_email: e.target.checked })}
            data-testid="pilot-invite-send-email"
            className="accent-[#FF5A1F]"
          />
          Also email the ask via Resend (else copy the link manually)
        </label>
        <button
          type="submit"
          disabled={creating}
          data-testid="pilot-invite-create-btn"
          className="sm:col-span-2 rounded-md bg-[#FF5A1F] text-black text-sm font-semibold px-4 py-2 hover:bg-[#FF7038] disabled:opacity-40 inline-flex items-center justify-center gap-1.5"
        >
          <PlusCircle size={13} weight="fill" /> {creating ? "Creating…" : "Create invite link"}
        </button>
      </form>

      <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold mb-2">
        Active invites {invites.length > 0 && `· ${invites.length}`}
      </div>
      {loading ? (
        <div className="text-neutral-500 text-xs">Loading…</div>
      ) : invites.length === 0 ? (
        <div className="text-neutral-500 text-xs">
          No pilot invites yet. Create one above — link is valid for 30 days.
        </div>
      ) : (
        <div className="space-y-1.5">
          {invites.slice(0, 8).map((inv) => (
            <div key={inv.id} data-testid={`pilot-invite-row-${inv.id}`}>
              <div
                className="grid grid-cols-1 sm:grid-cols-[1.5fr_2fr_auto_auto_auto_auto] gap-2 items-center rounded-md border border-white/5 bg-black/40 px-3 py-2 text-xs"
              >
                <div className="min-w-0">
                  <div className="text-white truncate" title={inv.email}>{inv.email}</div>
                  <div className="text-neutral-500 truncate">
                    {inv.invite_person_name || "—"}
                    {inv.invite_company_name ? ` · ${inv.invite_company_name}` : ""}
                  </div>
                </div>
                <div className="min-w-0 font-mono text-[10px] text-neutral-400 truncate" title={inv.submit_url}>
                  {inv.submit_url}
                </div>
                <span
                  className={`px-2 py-0.5 rounded-full text-[10px] uppercase tracking-widest font-semibold border ${
                    inv.status === "submitted"
                      ? "text-emerald-300 border-emerald-400/40 bg-emerald-400/10"
                      : "text-[#FF9764] border-[#FF5A1F]/40 bg-[#FF5A1F]/10"
                  }`}
                >
                  {inv.status}
                </span>
                <button
                  type="button"
                  onClick={() => toggleVinyl(inv)}
                  disabled={vinylBusy === inv.token}
                  data-testid={`pilot-invite-vinyl-toggle-${inv.id}`}
                  title={inv.vinyl_priority ? "Remove from vinyl kit" : "Add to vinyl kit — order physical stickers"}
                  className={`text-[10px] uppercase tracking-widest border rounded-full px-2.5 py-1 transition-colors inline-flex items-center gap-1 ${
                    inv.vinyl_priority
                      ? "border-amber-300/50 bg-amber-300/10 text-amber-200"
                      : "border-white/10 text-neutral-400 hover:text-amber-200 hover:border-amber-300/40"
                  } ${vinylBusy === inv.token ? "opacity-40" : ""}`}
                >
                  <Star size={11} weight={inv.vinyl_priority ? "fill" : "regular"} />
                  {inv.vinyl_priority ? "Vinyl" : "Vinyl?"}
                </button>
                <button
                  type="button"
                  onClick={() => setExpandedQrToken(expandedQrToken === inv.token ? null : inv.token)}
                  data-testid={`pilot-invite-qr-toggle-${inv.id}`}
                  title="Show QR — perfect for truck shows / conferences"
                  className={`text-[10px] uppercase tracking-widest border rounded-full px-2.5 py-1 transition-colors inline-flex items-center gap-1 ${
                    expandedQrToken === inv.token
                      ? "border-[#FF5A1F]/50 bg-[#FF5A1F]/10 text-[#FF9764]"
                      : "border-white/10 text-neutral-400 hover:text-white hover:border-white/30"
                  }`}
                >
                  <QrCode size={11} weight="bold" /> {expandedQrToken === inv.token ? "Hide QR" : "QR"}
                </button>
                <button
                  type="button"
                  onClick={() => copyLink(inv.token, inv.submit_url)}
                  data-testid={`pilot-invite-copy-${inv.id}`}
                  className="text-[10px] uppercase tracking-widest text-neutral-400 hover:text-white border border-white/10 hover:border-white/30 rounded-full px-2.5 py-1 transition-colors"
                >
                  {copiedToken === inv.token ? "Copied!" : "Copy link"}
                </button>
              </div>
              {/* Expandable QR panel — shown when the QR toggle is active for this row.
                  White background so mobile scanners have max contrast. */}
              {expandedQrToken === inv.token && (
                <div
                  data-testid={`pilot-invite-qr-panel-${inv.id}`}
                  className="mt-1.5 rounded-md border border-white/5 bg-white/[0.02] p-4 flex flex-col sm:flex-row items-start sm:items-center gap-4"
                >
                  <div className="rounded-lg bg-white p-3 shrink-0">
                    <QRCodeSVG
                      value={inv.submit_url}
                      size={144}
                      level="M"
                      includeMargin={false}
                      data-testid={`pilot-invite-qr-svg-${inv.id}`}
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[10px] uppercase tracking-widest text-[#FF9764] font-semibold mb-1">
                      Flash-scan-ready
                    </div>
                    <div className="text-sm text-white">
                      Point a phone camera at this QR to jump straight to the quote form.
                    </div>
                    <div className="text-[11px] text-neutral-500 mt-1.5 leading-snug">
                      Great for truck-show booths, conference badges, or in-person pilot
                      onboarding — the pilot never has to type a URL. Link is valid for 30 days.
                    </div>
                    <div className="mt-2 text-[10px] font-mono text-neutral-500 truncate" title={inv.submit_url}>
                      {inv.submit_url}
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* ─── Vinyl Kit ─── */}
      {vinylKit && vinylKit.count > 0 && (
        <div
          data-testid="vinyl-kit-section"
          className="mt-5 rounded-lg border border-amber-300/25 bg-amber-300/[0.03] p-4"
        >
          <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-amber-200 font-semibold mb-1 inline-flex items-center gap-1.5">
                <Star size={11} weight="fill" /> QR Vinyl Kit · {vinylKit.unordered_count > 0 ? "Ready to order" : "All ordered"}
              </div>
              <div className="text-sm text-white font-medium">
                {vinylKit.count} of {vinylKit.capacity} pilots marked
                {vinylKit.unordered_count > 0 && ` · ${vinylKit.unordered_count} unordered · ~$${vinylKit.estimated_cost_usd} at Sticker Mule`}
                {vinylKit.ordered_count > 0 && vinylKit.unordered_count === 0 && ` · already ordered`}
              </div>
              <div className="text-[11px] text-neutral-500 mt-1 leading-snug max-w-lg">
                Download each design as a print-ready 3" circle PNG (QR + logo + name) — drops
                straight into Sticker Mule's design canvas, skips the layout step.
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <a
                href={`${process.env.REACT_APP_BACKEND_URL || ""}/api/admin/pilot-quote-invites/vinyl-kit/download-all.zip`}
                data-testid="vinyl-kit-download-zip"
                className="rounded-full border border-white/15 hover:border-white/40 text-neutral-300 hover:text-white text-[10px] uppercase tracking-widest px-3 py-1.5 inline-flex items-center gap-1.5 transition-colors"
                title="Download all designs as a ZIP"
              >
                <Printer size={11} weight="bold" /> All PNGs
              </a>
              <button
                onClick={handleOrderVinyl}
                data-testid="vinyl-kit-order-btn"
                disabled={vinylKit.unordered_count === 0}
                className="rounded-full bg-amber-300 text-black text-xs font-bold px-4 py-2 hover:bg-amber-200 disabled:opacity-30 disabled:cursor-not-allowed inline-flex items-center gap-1.5"
              >
                <Printer size={12} weight="bold" />
                {vinylKit.unordered_count > 0 ? "Order via Sticker Mule →" : "Nothing new to order"}
              </button>
            </div>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {vinylKit.invites.map((v) => (
              <a
                key={v.id}
                href={`${process.env.REACT_APP_BACKEND_URL || ""}/api/admin/pilot-quote-invites/${v.token}/vinyl-design.png`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`vinyl-kit-chip-${v.id}`}
                title={v.vinyl_ordered_at
                  ? `Ordered ${new Date(v.vinyl_ordered_at).toLocaleDateString()} · click to re-download design`
                  : `Click to download print-ready PNG for ${v.email}`}
                className={`px-2.5 py-1 rounded-full border text-[11px] inline-flex items-center gap-1.5 transition-colors hover:border-amber-200 ${
                  v.vinyl_ordered_at
                    ? "border-emerald-400/30 bg-emerald-400/[0.05] text-neutral-300"
                    : "border-amber-300/25 bg-black/50 text-neutral-200"
                }`}
              >
                {v.vinyl_ordered_at
                  ? <CheckCircle size={10} weight="fill" className="text-emerald-300" />
                  : <Star size={10} weight="fill" className="text-amber-200" />}
                {v.invite_person_name || v.email}
              </a>
            ))}
          </div>

          {/* Shop-Poster download row — larger printable variants of the
              same QR so pilots can hang one over the shop desk, garage
              bay, or full wall. Rendered at 150 DPI on demand. */}
          <div className="mt-4 pt-3 border-t border-amber-300/15" data-testid="poster-download-row">
            <div className="text-[10px] uppercase tracking-widest text-amber-200 font-semibold mb-2 inline-flex items-center gap-1.5">
              <Printer size={11} weight="bold" /> Shop poster — printable QR for the garage wall
            </div>
            <p className="text-[11px] text-neutral-500 mb-2 max-w-lg">
              Pick a size, tap a pilot name, PNG downloads at 150 DPI print-ready
              resolution. Uses <code className="text-neutral-400">utm_campaign=shop-poster</code> so
              wall scans stay separate from truck-sticker scans in the founder dashboard.
            </p>
            {["11x17", "18x24", "24x36"].map((size) => (
              <div key={size} className="mb-2">
                <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1">
                  {size.replace("x", "×")}"
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {vinylKit.invites.map((v) => (
                    <a
                      key={`${size}-${v.id}`}
                      href={`${process.env.REACT_APP_BACKEND_URL || ""}/api/admin/pilot-quote-invites/${v.token}/poster.png?size=${size}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`poster-chip-${size}-${v.id}`}
                      title={`Download ${size.replace("x", "×")}" poster for ${v.email}`}
                      className="px-2.5 py-1 rounded-full border border-white/10 bg-black/50 text-neutral-300 text-[10px] inline-flex items-center gap-1.5 transition-colors hover:border-amber-200 hover:text-white"
                    >
                      <Printer size={9} weight="bold" />
                      {v.invite_person_name || v.email}
                    </a>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}


/* ─── PendingPhotosPanel ───
   Thumbs-up / thumbs-down each self-submitted photo BEFORE it leaks to
   the public /customers page. The public-feed endpoint scrubs photos
   where `photo_approved !== true`, so the panel is the gate. */
function PendingPhotosPanel({ onReload }) {
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const backendBase = process.env.REACT_APP_BACKEND_URL || "";

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await client.get("/admin/testimonials/pending-photos");
      setPending(data.pending || []);
    } catch (e) {
      // silent
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const approve = async (id) => {
    setBusyId(id);
    try {
      await client.post(`/admin/testimonials/${id}/approve-photo`);
      toast.success("Photo approved — now visible on /customers");
      await load();
      onReload?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't approve");
    } finally {
      setBusyId(null);
    }
  };

  const reject = async (id) => {
    setBusyId(id);
    try {
      await client.post(`/admin/testimonials/${id}/reject-photo`);
      toast.success("Photo rejected — quote is kept but the image was scrubbed");
      await load();
      onReload?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't reject");
    } finally {
      setBusyId(null);
    }
  };

  if (!loading && pending.length === 0) {
    return null;  // panel hides itself when there's nothing to review
  }

  return (
    <div
      data-testid="pending-photos-panel"
      className="rounded-2xl border border-amber-400/30 bg-amber-400/[0.03] p-5"
    >
      <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
        <div>
          <div className="overline mb-1 flex items-center gap-2 text-amber-300">
            <Warning size={11} weight="fill" /> AWAITING PHOTO REVIEW · {pending.length}
          </div>
          <h3 className="font-display text-lg font-semibold">
            Self-submitted photos need your thumbs-up before going live
          </h3>
          <p className="text-neutral-500 text-xs mt-1 leading-relaxed max-w-xl">
            Pilots dropped these photos through their testimonial link. They stay hidden
            from /customers until you approve — that way no rogue selfie or partial-nudity
            surprise leaks to the marketing page.
          </p>
        </div>
      </div>

      {loading ? (
        <div className="text-neutral-500 text-xs">Loading…</div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {pending.map((t) => (
            <div
              key={t.id}
              data-testid={`pending-photo-row-${t.id}`}
              className="rounded-lg border border-white/10 bg-black/50 p-4 flex flex-col gap-3"
            >
              <div className="flex items-start gap-3">
                <img
                  src={`${backendBase}${t.photo_preview_url}`}
                  alt={t.person}
                  data-testid={`pending-photo-preview-${t.id}`}
                  className="w-16 h-16 rounded-full object-cover border border-white/15 shrink-0"
                />
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-white truncate">{t.person}</div>
                  <div className="text-[11px] text-neutral-500 truncate">{t.role}</div>
                  {t.source_email && (
                    <div className="text-[10px] text-neutral-600 mt-0.5 truncate">{t.source_email}</div>
                  )}
                </div>
              </div>
              <div className="text-[12px] text-neutral-300 leading-relaxed line-clamp-4 italic">
                "{t.quote}"
              </div>
              <div className="flex gap-2 pt-1">
                <button
                  onClick={() => reject(t.id)}
                  disabled={busyId === t.id}
                  data-testid={`pending-photo-reject-${t.id}`}
                  className="flex-1 rounded-md border border-red-400/30 bg-red-400/10 text-red-300 text-[11px] uppercase tracking-widest font-semibold py-2 hover:bg-red-400/20 disabled:opacity-40 inline-flex items-center justify-center gap-1.5"
                >
                  <ThumbsDown size={11} weight="fill" /> Reject
                </button>
                <button
                  onClick={() => approve(t.id)}
                  disabled={busyId === t.id}
                  data-testid={`pending-photo-approve-${t.id}`}
                  className="flex-1 rounded-md border border-emerald-400/40 bg-emerald-400/10 text-emerald-300 text-[11px] uppercase tracking-widest font-semibold py-2 hover:bg-emerald-400/20 disabled:opacity-40 inline-flex items-center justify-center gap-1.5"
                >
                  <ThumbsUp size={11} weight="fill" /> Approve
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

